import * as React from 'react';
import { bindActionCreators, Dispatch } from 'redux';
import { connect } from 'react-redux';
import {
    Button, Header, Input, Modal,
} from 'semantic-ui-react';
import { tl } from 'common/services/i18nService';
import { DateTimeOption } from 'common/components/dateTimePicker/DateTimePicker';
import datePickerService from 'common/services/datePickerService';
import moment from 'moment';
import { SIMPLE_DATE_FORMAT_STRING_NO_SECONDS } from 'common/store/common/constants';
import DOMPurify from 'dompurify';
import { pushAlert } from 'common/components/notifications/actions';
import { Alert, AlertLevel } from 'common/components/notifications/types';
import { closeModal } from './actions';
import { ModalProperties } from './reducer';
import { updateExplanationDeadline } from '../assessment/actions';
import {
    cancelExplanationRecording,
    desistCancelExplanationRecording,
    desistRedoExplanationRecording,
    redoExplanationRecording,
    setFilenameForRecording,
} from '../../features/recordingHeader/actions';
import { utils as explanationRecordingUtils } from '../../features/recordingHeader/utils';

import apiService from '../../services/api/apiService';
import { ExplanationObject } from '../assessment/reducer';
import { RootState } from '../rootReducer';
import { ExplanationOfGradesRequestStatus, ExplanationOfGradesStatus } from '../../features/table/types';
import { ContextState } from '../context/reducer';
import { sendExplanation } from '../../features/explanationOfGrades/actions';

interface ModalDialogAttributes {
    showModal: boolean;
    properties: ModalProperties;
    explanationSettings: ExplanationObject;
    showCancelButton: boolean;
    closeOnEscape: boolean;
    closeOnDimmerClick: boolean;
    context: ContextState;
}

interface ModalDialogDispatch {
    closeModal: () => void;
    updateExplanationDeadline: (newDeadline: string) => void;
    cancelExplanationRecording: () => void;
    desistCancelExplanationRecording: () => void;
    redoExplanationRecording: () => void;
    desistRedoExplanationRecording: () => void;
    setFilenameForRecording: (filename: string) => void;
    sendExplanation: (featureId: string, sendToAllCandidates?: boolean) => void;
    pushAlert: (alertModel: Alert) => void;
}

interface ModalDialogState {
    newDate: string;
}

type ModalDialogProps = ModalDialogAttributes & ModalDialogDispatch;

class ModalDialog extends React.PureComponent<ModalDialogProps, ModalDialogState> {
    recordingExplanationFilenameInputElement: React.RefObject<Input>;

    constructor(props: ModalDialogProps) {
        super(props);

        this.recordingExplanationFilenameInputElement = React.createRef();
        this.state = {
            newDate: '',
        };
    }

    confirmDialog = () => {
        const {
            context, properties, closeModal, updateExplanationDeadline, cancelExplanationRecording,
            redoExplanationRecording, setFilenameForRecording, sendExplanation, pushAlert,
        } = this.props;
        const { name, hitData, explanationRecordingDefaultFilename } = properties;
        switch (name) {
        case 'cancelRequest': {
            apiService.updateExplanationOfGradesOrRequestStatus(hitData.assessmentRunId,
                hitData.userAssessmentId,
                hitData.partyId,
                hitData.committeeId,
                ExplanationOfGradesRequestStatus.NOT_REQUESTED,);
            break;
        }
        case 'request': {
            apiService.updateExplanationOfGradesOrRequestStatus(hitData.assessmentRunId,
                hitData.userAssessmentId,
                hitData.partyId,
                hitData.committeeId,
                ExplanationOfGradesRequestStatus.REQUESTED,);
            break;
        }
        case 'editDeadline': {
            if (this.state && this.state.newDate) {
                updateExplanationDeadline(this.state.newDate);
            }
            break;
        }
        case 'confirmCancelExplanationRecording': {
            cancelExplanationRecording();
            break;
        }
        case 'confirmRedoExplanationRecording': {
            redoExplanationRecording();
            break;
        }
        case 'setFilenameForRecording': {
            const filename = (this.recordingExplanationFilenameInputElement.current as any).inputRef.current.value;
            const finalFilename = explanationRecordingUtils.injectFileFormat(filename, explanationRecordingDefaultFilename);
            setFilenameForRecording(finalFilename);
            break;
        }
        case 'sendExplanationToCandidate':
            // when this is action is called, it can only be from the explanation workspace,
            // so we can use action because the sagas is watching
            sendExplanation(properties.featureId, false);
            break;
        case 'sendExplanationToAllCandidates':
            // if this is triggered from the explanations overview feature action then the explanation saga is not started,
            // so we must call the api service
            const promise = apiService.updateExplanationOfGradesOrRequestStatus(
                context.assessmentRunId,
                -1,
                -1,
                context.selectedCommitteeId > 0 ? context.selectedCommitteeId : undefined,
                ExplanationOfGradesStatus.PUBLISHED,
                true,
            );
            promise.then((response) => {
                const numberOfCandidatesSentTo = response.explanation.filter((expl: any) => expl.success && !expl.safeToUpdateStatusButFailed).length;
                const numberOfCandidatesFailedSending = response.explanation.filter((expl: any) => expl.safeToUpdateStatusButFailed).length;
                if (numberOfCandidatesSentTo > 0) {
                    let messageText = tl('grading.explanationOfGrades.sentToAllResponseSuccess.info', {
                        numberOfCandidatesSentTo,
                        numberOfCandidatesReady: numberOfCandidatesSentTo + numberOfCandidatesFailedSending,
                    });
                    if (numberOfCandidatesFailedSending === 0) {
                        messageText = tl('grading.explanationOfGrades.sentToAllResponseSuccess.allSentInfo');
                    }
                    pushAlert({
                        level: AlertLevel.SUCCESS,
                        key: tl('grading.explanationOfGrades.sentToAllResponseSuccess.title'),
                        info: messageText,
                    });
                }
                if (numberOfCandidatesFailedSending > 0) {
                    pushAlert({
                        level: AlertLevel.WARNING,
                        key: tl('grading.explanationOfGrades.sentToAllResponseFailed.title'),
                        info: tl('grading.explanationOfGrades.sentToAllResponseFailed.info', {
                            numberOfCandidatesFailedSending,
                        }),
                    });
                }
            });
            break;
        }
        closeModal();
    };

    doCloseModal = () => {
        const {
            properties, closeModal, desistCancelExplanationRecording, desistRedoExplanationRecording,
        } = this.props;
        const { name } = properties;

        switch (name) {
        case 'confirmCancelExplanationRecording': {
            desistCancelExplanationRecording();
            break;
        }
        case 'confirmRedoExplanationRecording': {
            desistRedoExplanationRecording();
            break;
        }
        }
        closeModal();
    };

    dateChanged = (newDeadline: string) => {
        const newDate = datePickerService.parseTimeFromUTC(newDeadline);
        this.setState({ newDate });
    };

    getDefaultDate = (): string => {
        const { explanationSettings } = this.props;
        const explanationDeadlineMoment = moment.utc(explanationSettings.explanationOfGradesDeadline, SIMPLE_DATE_FORMAT_STRING_NO_SECONDS);
        const isExplanationDateValid: boolean = explanationDeadlineMoment.isValid();
        return explanationSettings && explanationSettings.explanationOfGradesDeadline && isExplanationDateValid
            ? explanationDeadlineMoment.local().format('YYYY-MM-DD')
            : moment().format('YYYY-MM-DD');
    };

    renderContent = (): any => {
        const { properties } = this.props;
        const {
            name, hitData, contentCaptionKey, explanationRecordingDefaultFilename,
        } = properties;
        switch (name) {
        case 'editDeadline':
            return (
                <div>
                    <p>
                        <strong>{tl('grading.explanationOfGrades.editDeadlineWarning')}</strong>
                        {`  ${tl('grading.explanationOfGrades.editDeadlineNote')}`}
                    </p>
                    <p>{tl('grading.explanationOfGrades.editDeadlineDescription')}</p>
                    <DateTimeOption
                        onlyDateInput
                        noLabel
                        open
                        defaultDateString={this.getDefaultDate()}
                        onFromConditionChange={this.dateChanged}
                    />
                </div>
            );
        case 'cancelRequest':
            return (
                <p>
                    {
                        `${tl('grading.explanationOfGrades.cancelRequestDialog')} ${hitData && hitData.partyName} ?`
                    }
                </p>
            );

        case 'request':
            return (
                <p>
                    {
                        `${tl('grading.explanationOfGrades.requestDialog')} ${hitData && hitData.partyName} ?`
                    }
                </p>
            );
        case 'setFilenameForRecording':
            return (
                <div>
                    <Input
                        type="text"
                        className=""
                        placeholder={explanationRecordingDefaultFilename}
                        size="mini"
                        ref={this.recordingExplanationFilenameInputElement}
                    />
                </div>
            );
        case 'sendExplanationToCandidate':
            return (
                <div dangerouslySetInnerHTML={
                    { __html: DOMPurify.sanitize(tl('grading.explanationOfGrades.send.warning')) }
                }
                />
            );
        case 'sendExplanationToAllCandidates':
            return (
                <div dangerouslySetInnerHTML={
                    { __html: DOMPurify.sanitize(tl('grading.explanationOfGrades.sendToAll.warning')) }
                }
                />
            );
        }
        return <p>{contentCaptionKey ? tl(contentCaptionKey) : tl('modal.genericSimpleConfirmationModal.content')}</p>;
    };

    render() {
        const {
            showModal, properties, showCancelButton, closeOnEscape, closeOnDimmerClick,
        } = this.props;
        if (!showModal || !properties) {
            return null;
        }
        return (
            <Modal
                open={showModal}
                size="small"
                onClose={this.doCloseModal}
                closeOnEscape={closeOnEscape}
                closeOnDimmerClick={closeOnDimmerClick}
            >
                <Header content={properties.title || 'grading.genericSimpleConfirmationModal.title'} />
                <Modal.Content>
                    {this.renderContent()}
                </Modal.Content>
                <Modal.Actions>
                    {showCancelButton ? (
                        <Button
                            color={properties.cancelButtonColor || 'red'}
                            onClick={this.doCloseModal}
                            content={tl(properties.cancelButtonCaptionKey || 'modal.genericSimpleConfirmationModal.cancel')}
                        />
                    ) : ''}
                    <Button
                        color={properties.confirmButtonColor || 'green'}
                        onClick={this.confirmDialog}
                        labelPosition={properties.icon || !properties.whithoutIcon ? 'right' : undefined}
                        icon={properties.whithoutIcon ? null : (properties.icon || 'checkmark')}
                        content={tl(properties.confirmButtonCaptionKey || 'modal.genericSimpleConfirmationModal.confirm')}
                    />
                </Modal.Actions>
            </Modal>
        );
    }
}

const mapStateToProps = (state: RootState): ModalDialogAttributes => ({
    showModal: state.modal.showModal,
    properties: state.modal.properties,
    explanationSettings: state.assessment.explanationSettings,
    closeOnEscape: state.modal.properties && !state.modal.properties.disallowCancel,
    closeOnDimmerClick: state.modal.properties && !state.modal.properties.disallowCancel,
    showCancelButton: state.modal.properties && !state.modal.properties.disallowCancel,
    context: state.context,
});

const mapDispatchToProps = (dispatch: Dispatch): ModalDialogDispatch => bindActionCreators(
    {
        closeModal,
        updateExplanationDeadline,
        cancelExplanationRecording,
        desistCancelExplanationRecording,
        redoExplanationRecording,
        desistRedoExplanationRecording,
        setFilenameForRecording,
        sendExplanation,
        pushAlert,
    },
    dispatch,
);
export default connect<ModalDialogAttributes, ModalDialogDispatch>(mapStateToProps, mapDispatchToProps)(ModalDialog);
